import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, KPI, Card, Button, Input, Textarea, Select, Label, Empty } from "@/components/Primitives";
import { Sparkline } from "@/components/Charts";
import { DonutChart } from "@/components/DonutChart";
import { StatusPill, invoiceStatusVariant, ticketStatusVariant } from "@/components/StatusPill";
import { useNavigate } from "react-router-dom";
import { Download, Smartphone, LifeBuoy, Plus, Paperclip, Check, AlertTriangle, ArrowRight, CreditCard, Rocket, Sparkles, Users, X, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function gbp(v) { return `£${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

function woStatusVariant(s) {
  if (s === "completed") return "success";
  if (s === "in_progress") return "primary";
  if (s === "cancelled") return "danger";
  if (s === "submitted") return "warning";
  return "default";
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/stats/customer-overview");
      setData(data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-muted">Loading…</div>;
  const { customer: c, kpis: k, apps, invoices, projects, tickets, usage_series, work_orders, wo_breakdown, inv_breakdown, outstanding_invoices = [] } = data;

  const woSegments = [
    { label: "Pending", value: wo_breakdown?.pending || 0, color: "#F59E0B" },
    { label: "Submitted", value: wo_breakdown?.submitted || 0, color: "#8B5CF6" },
    { label: "In progress", value: wo_breakdown?.in_progress || 0, color: "hsl(var(--primary))" },
    { label: "Completed", value: wo_breakdown?.completed || 0, color: "#10B981" },
    { label: "Cancelled", value: wo_breakdown?.cancelled || 0, color: "#EF4444" },
  ];
  const woTotal = woSegments.reduce((s, x) => s + x.value, 0);

  const invSegments = [
    { label: "Paid", value: inv_breakdown?.paid || 0, color: "#10B981" },
    { label: "Pending", value: inv_breakdown?.pending || 0, color: "#F59E0B" },
    { label: "Overdue", value: inv_breakdown?.overdue || 0, color: "#EF4444" },
  ];
  const invTotal = invSegments.reduce((s, x) => s + x.value, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={`${c?.company} · Home`}
        title={`Welcome back, ${(() => { const n = user?.name?.split(" ")[0] || "there"; return n.charAt(0).toUpperCase() + n.slice(1); })()}`}
        subtitle={`Here's everything happening with your account today.`}
        actions={
          <>
            <Button variant="secondary" onClick={() => nav("/my-tickets?new=1")}><LifeBuoy className="w-4 h-4" strokeWidth={1.6}/>New ticket</Button>
            <Button onClick={() => nav("/my-projects")}>My projects</Button>
          </>
        }
      />

      {k.outstanding > 0 && (
        <OutstandingInvoicesBanner
          total={k.outstanding}
          count={k.outstanding_count}
          oldestDays={k.oldest_overdue_days}
          invoices={outstanding_invoices}
          onViewAll={() => nav("/my-invoices")}
        />
      )}

      <RecentlyShippedStrip onOpen={() => nav("/my-roadmap")} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <KPI label="Active projects" value={k.active_projects} variant="primary" />
        <KPI label="App users (30d)" value={k.active_users.toLocaleString()} sub={`${k.apps} ${k.apps === 1 ? "app" : "apps"}`} />
        <KPI label="Outstanding" value={gbp(k.outstanding)} variant={k.outstanding > 0 ? "warning" : "success"} />
        <KPI label="Lifetime spend" value={gbp(k.lifetime_spend)} />
      </div>

      {/* Top row: Submit WO + Work Orders Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <SubmitWorkOrder projects={projects} onCreated={load} />
        </div>
        <Card className="lg:col-span-2" title="Work orders overview" right={<Select defaultValue="all" className="w-[120px] h-8 text-[12px]"><option value="all">All time</option></Select>}>
          <div className="p-5 flex items-center justify-center">
            <DonutChart segments={woSegments} size={200} centerValue={woTotal} centerLabel="Total" />
          </div>
        </Card>
      </div>

      {/* Second row: My WOs + Invoice snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3" title="My work orders" right={<button className="text-[12px] text-[hsl(var(--primary))] font-semibold" onClick={() => nav("/my-projects")}>View on projects</button>}>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                  <th className="text-left font-semibold px-5 py-3">Title</th>
                  <th className="text-left font-semibold px-5 py-3">Priority</th>
                  <th className="text-left font-semibold px-5 py-3">Due</th>
                  <th className="text-left font-semibold px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {(work_orders || []).slice(0, 6).map((w) => (
                  <tr key={w.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))]">
                    <td className="px-5 py-3 font-medium">{w.title}</td>
                    <td className="px-5 py-3"><StatusPill variant={w.priority === "Urgent" ? "danger" : w.priority === "High" ? "warning" : "default"}>{w.priority}</StatusPill></td>
                    <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{w.due_date ? new Date(w.due_date).toLocaleDateString() : "—"}</td>
                    <td className="px-5 py-3"><StatusPill variant={woStatusVariant(w.status)}>{w.status.replace("_", " ")}</StatusPill></td>
                  </tr>
                ))}
                {(!work_orders || work_orders.length === 0) && (
                  <tr><td colSpan={4}><Empty title="No work orders yet" subtitle="Submit your first one from the form above."/></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="lg:col-span-2" title="Invoice snapshot" right={<button className="text-[12px] text-[hsl(var(--primary))] font-semibold" onClick={() => nav("/my-invoices")}>View all</button>}>
          <div className="p-5 flex items-center justify-center">
            <DonutChart segments={invSegments} size={200} centerValue={invTotal} centerLabel="Invoices" />
          </div>
        </Card>
      </div>

      {/* App usage + tickets row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="App usage · last 7 days" className="lg:col-span-2" right={<span className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">Sessions</span>}>
          <div className="p-5">
            <Sparkline data={usage_series.map((u) => u.sessions)} height={120} />
            <div className="flex justify-between mt-3">
              {usage_series.map((u, i) => (
                <div key={i} className="flex flex-col items-center text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                  <span>{u.day}</span>
                  <span className="font-mono text-[11px] text-[hsl(var(--text))] mt-0.5">{u.sessions}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="Support" right={<Button size="sm" variant="secondary" onClick={() => nav("/my-tickets?new=1")}><Plus className="w-3.5 h-3.5" strokeWidth={2}/>New</Button>}>
          <div className="divide-y divide-[hsl(var(--border))]">
            {tickets.map((t) => (
              <div key={t.id} className="px-5 py-3 hover:bg-[hsl(var(--surface-hover))] cursor-pointer" onClick={() => nav("/my-tickets")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">{t.subject}</div>
                    <div className="text-[11px] text-[hsl(var(--text-muted))]">{t.number}</div>
                  </div>
                  <StatusPill variant={ticketStatusVariant(t.status)}>{t.status.replace("_", " ")}</StatusPill>
                </div>
              </div>
            ))}
            {tickets.length === 0 && <Empty title="No tickets" />}
          </div>
        </Card>
      </div>

      {/* Apps + Recent invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Your apps" right={<button className="text-[12px] text-[hsl(var(--primary))] font-semibold" onClick={() => nav("/my-apps")}>View all</button>}>
          <div className="divide-y divide-[hsl(var(--border))]">
            {apps.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-5 py-3 hover:bg-[hsl(var(--surface-hover))] cursor-pointer" onClick={() => nav("/my-apps")}>
                <img src={a.icon_url} alt="" className="w-9 h-9 rounded-lg object-cover ring-1 ring-[hsl(var(--border))]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate">{a.name}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">v{a.version} · {a.stats?.active_users?.toLocaleString() || 0} users</div>
                </div>
                <Smartphone className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
              </div>
            ))}
            {apps.length === 0 && <Empty title="No apps linked yet" />}
          </div>
        </Card>

        <Card title="Recent invoices" right={<button className="text-[12px] text-[hsl(var(--primary))] font-semibold" onClick={() => nav("/my-invoices")}>View all</button>}>
          <div className="divide-y divide-[hsl(var(--border))]">
            {invoices.map((i) => (
              <div key={i.id} className="flex items-center gap-3 px-5 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold font-mono">{i.number}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">Issued {new Date(i.issued_date).toLocaleDateString()}</div>
                </div>
                <StatusPill variant={invoiceStatusVariant(i.status)}>{i.status}</StatusPill>
                <div className="font-mono font-semibold text-[13px] w-[80px] text-right">{gbp(i.amount)}</div>
                <button className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))]" title="Download"><Download className="w-4 h-4" strokeWidth={1.6}/></button>
              </div>
            ))}
            {invoices.length === 0 && <Empty title="No invoices yet" />}
          </div>
        </Card>
      </div>

      <InterestCTA customerName={user?.customer_name} customerId={user?.customer_id}/>
    </div>
  );
}

function InterestCTA({ customerName, customerId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl relative overflow-hidden border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary-muted))] via-[hsl(var(--surface))] to-[hsl(var(--surface))]" data-testid="interest-cta">
      <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.22), transparent 65%)" }}/>
      <div className="relative p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5">
        <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center shrink-0 shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.6)]">
          <Sparkles className="w-5 h-5" strokeWidth={2}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--primary))] font-semibold mb-1 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" strokeWidth={2}/>Work with Soubor
          </div>
          <h3 className="text-[17px] font-bold tracking-tight">Need something like Soubor One for your business?</h3>
          <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1 max-w-xl leading-relaxed">
            Got another venture, side project, or team that needs the same kind of workspace? Tell us a little about it and we'll be in touch.
            <span className="block mt-1.5">Or, if you know someone we'd be perfect for, send them our way too. Successful introductions are rewarded — a month free, or a finder's fee.</span>
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="shrink-0 whitespace-nowrap" data-testid="open-interest-form">
          <Send className="w-3.5 h-3.5" strokeWidth={2}/>Tell us about it
        </Button>
      </div>
      {open && <InterestModal onClose={() => setOpen(false)} referrerName={customerName} referrerId={customerId}/>}
    </div>
  );
}

function InterestModal({ onClose, referrerName, referrerId }) {
  const [forSelf, setForSelf] = useState(true);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);

  // Pre-fill self details from logged-in customer when "for me" is selected
  useEffect(() => {
    if (forSelf && referrerName) {
      setForm((f) => ({ ...f, name: referrerName, company: "" }));
    } else {
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    }
    // eslint-disable-next-line
  }, [forSelf]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const tag = forSelf ? "[Self / new venture]" : "[Referral]";
      const fullMessage = [tag, form.message.trim()].filter(Boolean).join(" — ");
      await api.post("/public/interest", {
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        message: fullMessage || null,
        source_customer_id: referrerId || null,
        source_customer_name: referrerName || null,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="interest-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" strokeWidth={2}/>Work with Soubor</div>
            <h3 className="text-lg font-semibold mt-0.5">{forSelf ? "Tell us what you need" : "Tell us about them"}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>

        {sent ? (
          <div className="p-8 text-center space-y-3" data-testid="interest-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <Check className="w-7 h-7" strokeWidth={2.5}/>
            </div>
            <h4 className="text-lg font-semibold">{forSelf ? "Got it. We'll be in touch" : "Thanks for the introduction"}</h4>
            <p className="text-[13px] text-[hsl(var(--text-muted))] max-w-sm mx-auto leading-relaxed">
              {forSelf
                ? <>We've logged your enquiry and we'll reach out within a couple of working days.</>
                : <>We've logged {form.name || "their"} details and we'll reach out within a couple of working days.{referrerName ? <> If they sign up, we'll be in touch about your reward.</> : null}</>
              }
            </p>
            <div className="pt-2">
              <Button onClick={onClose} data-testid="interest-close">Done</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="p-6 space-y-4">
              {/* Who's this for? */}
              <div>
                <Label>Who's this for?</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="interest-audience">
                  <button
                    type="button"
                    onClick={() => setForSelf(true)}
                    className={`text-left px-3 py-2.5 rounded-lg border text-[12.5px] transition-all ${forSelf ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))] text-[hsl(var(--text))]" : "border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"}`}
                    data-testid="interest-for-self"
                  >
                    <div className="font-semibold">For me / a venture of mine</div>
                    <div className="text-[11px] mt-0.5 opacity-80">Another business or team that needs Soubor One.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForSelf(false)}
                    className={`text-left px-3 py-2.5 rounded-lg border text-[12.5px] transition-all ${!forSelf ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))] text-[hsl(var(--text))]" : "border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"}`}
                    data-testid="interest-for-other"
                  >
                    <div className="font-semibold">For someone I know</div>
                    <div className="text-[11px] mt-0.5 opacity-80">Refer a contact and you can earn a reward.</div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{forSelf ? "Your name *" : "Their name *"}</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={forSelf ? "Your name" : "e.g. Jane Smith"} data-testid="interest-name"/>
                </div>
                <div>
                  <Label>Company</Label>
                  <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={forSelf ? "Your other business" : "e.g. Acme Ltd"} data-testid="interest-company"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{forSelf ? "Best email *" : "Their email *"}</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={forSelf ? "you@yourbusiness.com" : "jane@acme.com"} data-testid="interest-email"/>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Optional"/>
                </div>
              </div>
              <div>
                <Label>{forSelf ? "What are you after? (optional)" : "What do they need? (optional)"}</Label>
                <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={forSelf ? "A quick sentence on what you'd like to build or run on Soubor One." : "A quick sentence on what they're after. A mobile app, SaaS build, support… anything at all."}/>
              </div>
              {!forSelf && referrerName && (
                <div className="text-[11.5px] text-[hsl(var(--text-muted))] italic">
                  We'll log this introduction under <span className="font-semibold not-italic text-[hsl(var(--text))]">{referrerName}</span>. If they become a customer, we'll thank you with a month free or a finder's fee — your choice.
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="interest-submit">
                {saving ? "Sending…" : (<><Send className="w-3.5 h-3.5" strokeWidth={2}/>Send to Soubor One</>)}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function SubmitWorkOrder({ projects, onCreated }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [due, setDue] = useState("");
  const [projectId, setProjectId] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/work-orders", {
        title, description: desc, priority,
        due_date: due ? new Date(due).toISOString() : null,
        project_id: projectId || null,
      });
      setTitle(""); setDesc(""); setPriority("Normal"); setDue(""); setProjectId("");
      toast.success("Work order submitted. We'll get on it");
      await onCreated?.();
    } catch (err) {
      toast.error("Failed to submit");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]" data-testid="submit-wo-form">
      <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
        <div>
          <h3 className="text-[14px] font-semibold tracking-tight">Submit a work order</h3>
          <p className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">Something you'd like us to build, fix, or tweak? Send it straight to us.</p>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Fast-track</div>
      </div>
      <div className="p-5 space-y-3">
        <div><Label>Title *</Label><Input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New landing page, bug fix, content update" data-testid="wo-title"/></div>
        <div><Label>Description *</Label><Textarea required value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="Describe what you need…" data-testid="wo-desc"/></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Priority</Label>
            <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
            </Select>
          </div>
          <div><Label>Due date (optional)</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)}/></div>
          <div>
            <Label>Project (optional)</Label>
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">No project</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="text-[11px] text-[hsl(var(--text-muted))] inline-flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" strokeWidth={1.6}/>Screenshots / file upload coming soon</div>
          <Button type="submit" disabled={saving} data-testid="submit-wo-btn">
            {saving ? "Submitting…" : <><Check className="w-4 h-4" strokeWidth={2}/>Submit</>}
          </Button>
        </div>
      </div>
    </form>
  );
}

function OutstandingInvoicesBanner({ total, count, oldestDays, invoices, onViewAll }) {
  const oldest = invoices[0];
  const hasOverdue = (oldestDays || 0) > 0;
  const payOldest = () => {
    if (!oldest) return;
    if (oldest.hosted_invoice_url) {
      window.open(oldest.hosted_invoice_url, "_blank", "noopener,noreferrer");
    } else {
      toast.info("We'll be in touch with payment details for this invoice.");
    }
  };

  return (
    <div
      className={`rounded-2xl border p-5 md:p-6 relative overflow-hidden ${
        hasOverdue
          ? "border-rose-300/60 dark:border-rose-500/30 bg-gradient-to-br from-rose-50 via-rose-50 to-amber-50 dark:from-rose-950/40 dark:via-rose-950/20 dark:to-amber-950/30"
          : "border-amber-300/60 dark:border-amber-500/30 bg-gradient-to-br from-amber-50 to-amber-50/50 dark:from-amber-950/40 dark:to-amber-950/10"
      }`}
      data-testid="outstanding-invoices-banner"
    >
      <div
        className="absolute -right-10 -top-10 w-48 h-48 rounded-full opacity-20 blur-3xl"
        style={{ background: hasOverdue ? "#F43F5E" : "#F59E0B" }}
      />
      <div className="relative flex flex-col md:flex-row md:items-center gap-5 md:gap-6">
        <div
          className={`shrink-0 w-12 h-12 rounded-xl grid place-items-center ${
            hasOverdue ? "bg-rose-500/15 text-rose-600 dark:text-rose-400" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
          }`}
        >
          {hasOverdue ? <AlertTriangle className="w-6 h-6" strokeWidth={2}/> : <CreditCard className="w-6 h-6" strokeWidth={2}/>}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-[hsl(var(--text-muted))]">
              {hasOverdue ? "Payment overdue" : "Invoices due"}
            </span>
            {hasOverdue && (
              <span className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-600 dark:text-rose-400">
                {oldestDays} {oldestDays === 1 ? "day" : "days"} past due
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl md:text-4xl font-mono font-bold tracking-tight">{gbp(total)}</span>
            <span className="text-[13px] text-[hsl(var(--text-muted))]">
              across {count} {count === 1 ? "invoice" : "invoices"}
            </span>
          </div>
          <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1">
            {hasOverdue
              ? "Some invoices are past due. Let's get these cleared."
              : "Thanks for keeping your account in good standing."}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {oldest && (
            <Button onClick={payOldest} data-testid="pay-oldest-invoice-btn">
              <CreditCard className="w-4 h-4" strokeWidth={2}/>
              Pay {oldest.number}
            </Button>
          )}
          <Button variant="secondary" onClick={onViewAll} data-testid="view-all-invoices-btn">
            View all
            <ArrowRight className="w-4 h-4" strokeWidth={2}/>
          </Button>
        </div>
      </div>

      {invoices.length > 1 && (
        <div className="relative mt-4 pt-4 border-t border-black/5 dark:border-white/10 flex items-center gap-4 overflow-x-auto no-scrollbar">
          {invoices.slice(0, 4).map((i) => (
            <button
              key={i.id}
              onClick={() => i.hosted_invoice_url && window.open(i.hosted_invoice_url, "_blank", "noopener,noreferrer")}
              className="shrink-0 flex items-center gap-3 px-3 py-2 rounded-lg bg-white/60 dark:bg-black/20 hover:bg-white dark:hover:bg-black/40 border border-black/5 dark:border-white/10 transition-colors text-left"
            >
              <div className="font-mono text-[11px] font-semibold">{i.number}</div>
              <div className="font-mono text-[12px] font-bold">{gbp(i.amount)}</div>
              <StatusPill variant={invoiceStatusVariant(i.status)}>{i.status}</StatusPill>
              {i.days_overdue > 0 && (
                <span className="text-[10px] font-semibold text-rose-600 dark:text-rose-400">
                  {i.days_overdue}d late
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}



function RecentlyShippedStrip({ onOpen }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/roadmap")
      .then(({ data }) => {
        const shipped = (data || [])
          .filter((r) => r.status === "shipped")
          .sort((a, b) => (b.shipped_date || "").localeCompare(a.shipped_date || ""))
          .slice(0, 3);
        setItems(shipped);
      })
      .catch(() => setItems([]));
  }, []);
  if (items.length === 0) return null;
  return (
    <div
      className="rounded-2xl border border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--surface))] to-[hsl(var(--primary-muted))]/30 p-5 hover:border-[hsl(var(--primary))] transition-colors cursor-pointer group"
      onClick={onOpen}
      data-testid="recently-shipped-strip"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] grid place-items-center">
            <Rocket className="w-4 h-4" strokeWidth={1.8}/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Just shipped</div>
            <div className="text-[14px] font-semibold">New in Soubor One</div>
          </div>
        </div>
        <div className="text-[11px] font-semibold text-[hsl(var(--primary))] inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
          See roadmap <ArrowRight className="w-3 h-3" strokeWidth={2}/>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.map((r) => (
          <div key={r.id} className="rounded-xl bg-[hsl(var(--background))] border border-[hsl(var(--border))] p-3" data-testid="shipped-item">
            <div className="inline-flex items-center gap-1 text-[10px] font-semibold text-[hsl(var(--success))] uppercase tracking-widest mb-1">
              <Check className="w-3 h-3" strokeWidth={2.5}/>{r.shipped_date ? new Date(r.shipped_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "Shipped"}
            </div>
            <div className="text-[13px] font-semibold leading-tight">{r.title}</div>
            {r.description && <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed line-clamp-2">{r.description}</div>}
            {(r.product_tags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {r.product_tags.slice(0, 2).map((t) => <span key={t} className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]">#{t}</span>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
